import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  hideNavBar,
  navigateToSettings,
  openModal,
  pinNavBar,
  redirectLocation,
  selectRedirectPathname,
  setLocation,
  startNewContact,
  startNewNote,
} from '@actions/ui';
import {
  createBrowserRouter,
  Outlet,
  redirect,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import Login from '@views/Auth/Login';
import Logout from '@views/Auth/Logout/Logout';
import Start from '@views/Start/Start';
import Onboarding from '@views/Onboarding';
import Tour from '@views/Tour';
import PrimaryLayout, { SettingsLayout } from '@layouts/PrimaryLayout';
import Search from '@views/Search';
import People from '@views/People';
import Group from '@views/Group';
import ContactV2 from '@views/Contact';
import ContactFormV2 from '@views/Contact/Form';
import SharedContact from '@views/SharedContact';
import Nexus from '@views/Nexus';
import Archive from '@views/Archive';
import NewGroup from '@views/Group/NewGroup';
import { AppErrorMessage, ErrorMessageWithNav } from '@views/ErrorBoundary';
import { ShareLayout } from '@layouts/MasterLayout';
import { getAppStartRoute } from '@support/app-start-route';
import EmailVerificationRedirect from '@views/EmailVerificationRedirect';
import SharedGroup from '@views/SharedGroup';
import Redirector from './Redirector';
import LoginCommunity from '@views/Auth/LoginCommunity';
import { ChatGPTOauthLogin } from '@views/Auth/OauthLogin';
import OauthCallback from '@views/Auth/OauthCallback';
import OpenInDesktop from '@views/OpenInDesktop';
import AppSumoLogin from '@views/Auth/AppSumoLogin';
import AddBirthday from '@views/AddBirthday';
import { SettingsView } from '@views/Settings';
import Activity from '@views/Activity';
import { useAuthContext } from '@support/hooks/use-auth-context';
import posthog from 'posthog-js';
import Notes from '@views/Notes';
import Map from '@views/Map';
import GroupActivity from '@views/Group/GroupActivity';
import { DesktopTriggeredUIAction } from '@app/types';
import { useViewToggling } from '@views/Settings/panels/General/Sidebar';
import { selectNavBarState } from '@reducers/ui/navBar';

const handleDesktopTriggeredUIAction = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isEnabled, toggleView } = useViewToggling();
  const { status: navBarStatus } = useSelector(selectNavBarState);

  useEffect(() => {
    let unsub = () => {};

    if (!!window.desktopIntegrations?.onTriggerAppUIAction) {
      unsub = window.desktopIntegrations.onTriggerAppUIAction(
        (action: DesktopTriggeredUIAction) => {
          switch (action) {
            case 'show-search-view':
              navigate('/search');
              break;

            case 'show-home-view':
              navigate('/');
              break;

            case 'show-people-view':
              navigate('/people');
              break;

            case 'show-notes-view':
              navigate('/notes');
              break;

            case 'show-starred-view':
              navigate('/group/starred');
              break;

            case 'show-nexus-view':
              navigate('/nexus');
              break;

            case 'toggle-notes-view':
              toggleView('showNotesInSidebar');
              break;

            case 'toggle-sidebar':
              if (navBarStatus === 'pinned') {
                dispatch(hideNavBar());
              } else {
                dispatch(pinNavBar());
              }
              break;

            case 'show-new-person-view':
              dispatch(startNewContact());
              break;

            case 'show-new-note-view':
              dispatch(startNewNote);
              break;

            case 'show-keyboard-shortcuts':
              window.app.emit('shortcuts:open');
              break;

            case 'show-settings-view':
              dispatch(navigateToSettings());
              break;

            case 'show-command-bar':
              dispatch(openModal('commandBar'));
              break;
          }
        },
      );
    }

    return unsub;
  }, [navigate, isEnabled('showNotesInSidebar'), navBarStatus]);
};

// Location and navigate can only be accessed within the browser router context, which is why we do this in a layout component
const RoutesWrapper = () => {
  const { user } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [didIdentify, setDidIdentify] = useState(false);
  const [recordingSession, setRecordingSession] = useState(false);
  const redirectPathname = useSelector(selectRedirectPathname);

  handleDesktopTriggeredUIAction();

  useEffect(() => {
    dispatch(setLocation(location));
  }, [location]);

  useEffect(() => {
    if (redirectPathname) {
      navigate(redirectPathname, { replace: true });
      dispatch(redirectLocation({ pathname: undefined }));
    }
  }, [redirectPathname, navigate]);

  useEffect(() => {
    const appStartRoute = getAppStartRoute();
    if (appStartRoute) {
      navigate(appStartRoute, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (user && location.pathname === '/onboarding' && !recordingSession) {
      if (!didIdentify) {
        posthog?.identify(String(user.id));
        setDidIdentify(true);
      }

      posthog?.set_config({ autocapture: true });
      posthog?.startSessionRecording();
      setRecordingSession(true);
    }

    if (!['/tour', '/onboarding'].includes(location.pathname) && recordingSession) {
      posthog?.set_config({ autocapture: false });
      posthog?.stopSessionRecording();
      setRecordingSession(false);
    }
  }, [user, location.pathname]);

  return <Outlet />;
};

// Because we have paths that are viewable both by users and non-users, we need to make a determination
// on whether or not to redirect to /login when we initially try to pull the user object for the current
// user. Otherwise we end up spamming the API when logged out users try to access protected routes (and
// we end up throwing unneccesary errors in the UI).
const publicPaths = new Set([
  'login',
  'app-sumo-login',
  'community-login',
  'oauth',
  'open-in-desktop',
  'share',
  'add-birthday',
]);

export function isPublicRoute(pathname: string) {
  let pathStart = pathname.split('/')[1] || '';
  if (pathStart.includes('?')) {
    pathStart = pathStart.split('?')[0];
  }
  return publicPaths.has(pathStart);
}

const redirectOnLoadTo = (path: string) => () => redirect(path);

export default function AppRoutes() {
  const router = createBrowserRouter([
    {
      element: <RoutesWrapper />,
      path: '',
      // Minimal catchall error UI for uncaught rendering/loader errors happening in the router. Errors occurring
      // in context providers that live outside of the router are caught by the AppErrorBoundary in App.tsx
      errorElement: <AppErrorMessage />,
      children: [
        {
          path: '/open-in-desktop/*',
          element: <OpenInDesktop />,
        },
        {
          path: '/share',
          element: <ShareLayout />,
          children: [
            { path: 'contact/:uuid', element: <SharedContact /> },
            { path: 'group/:uuid', element: <SharedGroup /> },
          ],
        },
        {
          path: '/add-birthday',
          element: <AddBirthday />,
        },
        {
          path: '/community-login',
          element: <LoginCommunity />,
        },
        {
          path: '/oauth/login',
          element: <ChatGPTOauthLogin />,
        },
        {
          path: '/oauth/notion/callback',
          element: <OauthCallback integration="notion" />,
        },
        {
          path: '/app-sumo-login',
          element: (
            <Redirector redirectOn={['userAuthenticated']}>
              <AppSumoLogin />
            </Redirector>
          ),
        },
        {
          path: '/login',
          element: (
            <Redirector redirectOn={['userAuthenticated']}>
              <Login />
            </Redirector>
          ),
        },
        {
          path: '/logout',
          element: (
            <Redirector redirectOn={['noUser']}>
              <Logout />
            </Redirector>
          ),
        },
        {
          path: '/start',
          element: (
            <Redirector redirectOn={['noUser']}>
              <Start />
            </Redirector>
          ),
        },
        {
          path: '/onboarding',
          element: (
            <Redirector redirectOn={['noUser']}>
              <Onboarding />
            </Redirector>
          ),
        },
        {
          element: (
            <Redirector redirectOn={['noUser', 'userNotOnboarded']}>
              <PrimaryLayout />
            </Redirector>
          ),
          children: [{ path: '/tour', element: <Tour /> }],
        },
        // {
        //   path: '/account-processing',
        //   element: (
        //     <Redirector redirectOn={['noUser', 'userNotOnboarded']}>
        //       <ImportingContacts />
        //     </Redirector>
        //   ),
        // },
        { path: '/settings/auth', loader: redirectOnLoadTo('/settings') },
        {
          path: '/settings',
          element: (
            <Redirector redirectOn={['noUser', 'userNotOnboarded']}>
              <SettingsLayout />
            </Redirector>
          ),
          children: [
            { path: '', element: <SettingsView /> },
            { path: ':panel', element: <SettingsView /> },
            { path: ':panel/:subpanel', element: <SettingsView /> },
          ],
        },
        {
          element: (
            <Redirector redirectOn={['noUser', 'userNotOnboarded']}>
              <PrimaryLayout renderUserCallouts />
            </Redirector>
          ),
          // Catchall error UI that includes navigation bar so users can easily escape to safety
          errorElement: <ErrorMessageWithNav />,
          children: [
            { path: '/', element: <Activity /> },
            { path: '/search', element: <Search /> },
            { path: '/notes', element: <Notes /> },
            // { path: '/activity', element: <Activity /> },
            { path: '/people', element: <People /> },
            { path: '/people/map', element: <Map /> },
            { path: '/archive', element: <Archive /> },
            { path: '/group/create', element: <NewGroup /> },
            { path: '/group/:id', element: <Group /> },
            { path: '/group/:id/highlights', element: <GroupActivity /> },
            { path: '/contact/:id', element: <ContactV2 /> },
            { path: '/contact/:id/form', element: <ContactFormV2 /> },
            { path: '/nexus', element: <Nexus /> },
            { path: '/nexus/:conversationID', element: <Nexus /> },
            { path: '/verify/:id', element: <EmailVerificationRedirect /> },
          ],
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}
